import React, { useEffect, useMemo, useState } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import {
  TrackingStatCard,
  LiveStatusBanner,
  TrackingFilters,
  FieldPersonnelList,
  MapView,
  ActivityFeed,
} from './components';
import {
  TrackingStat,
  Personnel,
  ActivityEvent,
  initialsOf,
  avatarBgFor,
  relativeTime,
} from './liveTrackingTypes';
import { useDriverLocationsStore } from '../../../store/driverLocationsStore';
import { DriverLocation } from '../../../services/driverLocationsApi';
import styles from './AdminLiveTracking.module.css';

const DriversIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" /></svg>
);
const StopsIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96C5 16.1 5.9 17 7 17h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63H19c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1 1 0 0023 5H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" /></svg>
);
const EtaIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z" /></svg>
);
const AlertIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" /></svg>
);
const statIcons = [DriversIcon, StopsIcon, EtaIcon, AlertIcon];

function adapt(loc: DriverLocation): Personnel {
  const name = loc.driver_name || `Driver #${loc.driver_id}`;
  const since = Date.now() - new Date(loc.captured_at).getTime();
  const status: Personnel['status'] = since > 15 * 60 * 1000
    ? 'Offline'
    : (loc.speed_kmh ?? 0) > 5 ? 'Driving' : 'At Store';
  return {
    name,
    initials: initialsOf(name),
    avatarBg: avatarBgFor(name),
    role: 'Driver',
    route: loc.route_name || '—',
    stopsCompleted: 0,
    totalStops: 0,
    status,
    lastSeen: relativeTime(loc.captured_at),
  };
}

const AdminLiveTracking: React.FC = () => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All Categories');
  const [route, setRoute] = useState('All Routes');

  const live = useDriverLocationsStore((s) => s.live);
  const fetchLive = useDriverLocationsStore((s) => s.fetchLive);

  useEffect(() => {
    fetchLive(15);
    const interval = setInterval(() => fetchLive(15), 30000);
    return () => clearInterval(interval);
  }, [fetchLive]);

  const personnel = useMemo<Personnel[]>(() => live.map(adapt), [live]);

  const filteredPersonnel = useMemo(() => {
    return personnel.filter((p) => {
      const q = search.trim().toLowerCase();
      const matchesSearch = !q || p.name.toLowerCase().includes(q) || p.route.toLowerCase().includes(q);
      const matchesCategory = category === 'All Categories' || p.role === category;
      const matchesRoute = route === 'All Routes' || p.route.includes(route);
      return matchesSearch && matchesCategory && matchesRoute;
    });
  }, [personnel, search, category, route]);

  const onlineCount = filteredPersonnel.filter((p) => p.status !== 'Offline').length;
  const offlineCount = filteredPersonnel.filter((p) => p.status === 'Offline').length;

  const trackingStats: TrackingStat[] = [
    { label: 'Active Drivers', value: String(onlineCount).padStart(2, '0'), valueColor: '#16A34A', iconBg: '#ECFDF5', iconColor: '#16A34A' },
    { label: 'Stops Completed', value: '—', valueColor: '#2563EB', iconBg: '#EFF6FF', iconColor: '#2563EB' },
    { label: 'ETA Accuracy', value: '—', valueColor: '#7C3AED', iconBg: '#EDE9FE', iconColor: '#7C3AED' },
    { label: 'Delay Alerts', value: String(offlineCount).padStart(2, '0'), valueColor: '#DC2626', iconBg: '#FEF2F2', iconColor: '#DC2626' },
  ];

  // Activity feed needs an audit-events backend; until then it's empty.
  const activityEvents: ActivityEvent[] = [];

  return (
    <div className={styles.container}>
      <div className={styles.statsGrid}>
        {trackingStats.map((stat, i) => (
          <TrackingStatCard key={stat.label} stat={stat} icon={statIcons[i]} />
        ))}
      </div>

      <LiveStatusBanner />

      <TrackingFilters
        search={search}
        onSearchChange={setSearch}
        category={category}
        onCategoryChange={setCategory}
        route={route}
        onRouteChange={setRoute}
      />

      <div className={styles.mainGrid}>
        <FieldPersonnelList items={filteredPersonnel} onlineCount={onlineCount} offlineCount={offlineCount} />
        <div className={styles.mapPanel}>
          <MapView />
          <ActivityFeed events={activityEvents} />
        </div>
      </div>
    </div>
  );
};

export const AdminLiveTrackingPage: React.FC = () => (
  <ProtectedRoute requiredRoles={['Admin', 'Driver', 'Customer']}>
    <AdminLiveTracking />
  </ProtectedRoute>
);

export { AdminLiveTracking };
export default AdminLiveTracking;
